import React, { useState, useEffect, useCallback } from 'react';
import {
  Truck, Plus, X, Loader2, Percent, Landmark,
  CheckCircle, AlertCircle, Printer, ChevronDown, ChevronUp, Wallet, Edit2,
  Paperclip, Trash2, RefreshCw
} from 'lucide-react';
import {
  facturasCompraService, retencionesService, proveedoresService, islrConceptosService, serviciosService,
  centrosOperacionService, configService, facturasCompraArchivosService, UPLOADS_BASE_URL
} from '../../../services/api';
import RetencionEmitidaIVAPrintModal from '../Factura/RetencionEmitidaIVAPrintModal';
import RetencionEmitidaISLRPrintModal from '../Factura/RetencionEmitidaISLRPrintModal';
import useModal from '../../../hooks/useModal';

const ESTADOS_FACTURA = {
  registrada: { label: 'Registrada', className: 'bg-slate-100 text-slate-700' },
  retenida: { label: 'Retenida', className: 'bg-amber-100 text-amber-700' },
  pagada: { label: 'Pagada', className: 'bg-emerald-100 text-emerald-700' },
  anulada: { label: 'Anulada', className: 'bg-red-100 text-red-700' }
};

const fmtMonto = (n, moneda = 'USD') => {
  const val = Number(n) || 0;
  return moneda === 'VES'
    ? `Bs. ${val.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : `$${val.toFixed(2)}`;
};
const fmtFecha = (f) => (f ? new Date(f).toLocaleDateString('es-VE') : '—');
const fmtPct = (n) => `${((Number(n) || 0) * 100).toFixed(2)}%`;

const FacturasCompraView = () => {
  const { notify, confirm, prompt, ModalRenderer } = useModal();
  const [facturas, setFacturas] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [islrConceptos, setIslrConceptos] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [centrosOperacion, setCentrosOperacion] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [proveedorFiltro, setProveedorFiltro] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('');
  const [search, setSearch] = useState('');

  const [showNueva, setShowNueva] = useState(false);
  const [facturaEditar, setFacturaEditar] = useState(null);
  const [facturaPago, setFacturaPago] = useState(null); // factura para la que se registra pago
  const [bancos, setBancos] = useState([]);
  const [facturaAbierta, setFacturaAbierta] = useState(null); // id expandido
  const [retencionesPorFactura, setRetencionesPorFactura] = useState({});
  const [cargandoRetenciones, setCargandoRetenciones] = useState(null);
  const [accionEnCurso, setAccionEnCurso] = useState(null);

  const [modalPreviewIVA, setModalPreviewIVA] = useState(null);
  const [modalPreviewISLR, setModalPreviewISLR] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const filtros = {};
      if (proveedorFiltro) filtros.proveedor_id = proveedorFiltro;
      if (estadoFiltro) filtros.estado = estadoFiltro;
      if (search) filtros.search = search;
      const [resFacturas, resStats] = await Promise.all([
        facturasCompraService.getAll(filtros),
        facturasCompraService.getStats()
      ]);
      setFacturas(resFacturas.data || []);
      setStats(resStats.data || null);
    } catch (err) {
      setError(err.message || 'Error al cargar facturas de compra');
    } finally {
      setLoading(false);
    }
  }, [proveedorFiltro, estadoFiltro, search]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    (async () => {
      try {
        const [resProv, resIslr, resServicios, resBancos, resCentros] = await Promise.all([
          proveedoresService.getAll({ activo: true }),
          islrConceptosService.getAll(),
          serviciosService.getAll({ activo: true }),
          proveedoresService.getBancos(),
          centrosOperacionService.getAll({ activo: true })
        ]);
        setProveedores(resProv.data || []);
        setIslrConceptos(resIslr.data || []);
        setServicios(resServicios.data || []);
        setBancos(resBancos.data || []);
        setCentrosOperacion(resCentros.data || []);
      } catch (err) {
        console.error('Error al cargar proveedores/conceptos ISLR/servicios:', err);
      }
    })();
  }, []);

  const cargarRetenciones = async (facturaCompraId) => {
    setCargandoRetenciones(facturaCompraId);
    try {
      const res = await retencionesService.getAll({ factura_compra_id: facturaCompraId });
      setRetencionesPorFactura((prev) => ({ ...prev, [facturaCompraId]: (res.data || []).filter(r => r.estado !== 'anulada') }));
    } catch (err) {
      await notify('error', 'Error', err.message || 'Error al cargar retenciones');
    } finally {
      setCargandoRetenciones(null);
    }
  };

  const toggleFactura = (factura) => {
    if (facturaAbierta === factura.id) {
      setFacturaAbierta(null);
      return;
    }
    setFacturaAbierta(factura.id);
    if (!retencionesPorFactura[factura.id]) cargarRetenciones(factura.id);
  };

  const handleRetenerIVA = async (factura) => {
    setAccionEnCurso(`iva-${factura.id}`);
    try {
      await retencionesService.crearEmitidaIVA(factura.id);
      await cargarRetenciones(factura.id);
      await cargar();
    } catch (err) {
      await notify('error', 'Error', err.message || 'Error al crear la retención de IVA');
    } finally {
      setAccionEnCurso(null);
    }
  };

  const handleRetenerISLR = async (factura) => {
    let conceptoId = factura.proveedor_islr_concepto_id;
    if (!conceptoId) {
      if (islrConceptos.length === 0) {
        await notify('warning', 'Atención', 'No hay conceptos ISLR cargados.');
        return;
      }
      const opciones = islrConceptos.map((c, i) => `${i + 1}. ${c.nombre}`).join('\n');
      const seleccion = await prompt(
        'Concepto ISLR',
        `El proveedor no tiene concepto ISLR asignado. Elige uno (número):\n${opciones}`,
        'Número de concepto',
        '1'
      );
      const idx = parseInt(seleccion, 10) - 1;
      if (!(idx >= 0 && idx < islrConceptos.length)) return;
      conceptoId = islrConceptos[idx].id;
    }

    setAccionEnCurso(`islr-${factura.id}`);
    try {
      await retencionesService.crearEmitidaISLR(factura.id, conceptoId);
      await cargarRetenciones(factura.id);
      await cargar();
    } catch (err) {
      await notify('error', 'Error', err.message || 'Error al crear la retención de ISLR');
    } finally {
      setAccionEnCurso(null);
    }
  };

  const handleEmitir = async (retencion, facturaId) => {
    setAccionEnCurso(`emitir-${retencion.id}`);
    try {
      const res = await retencionesService.emitir(retencion.id);
      await notify('success', 'Listo', res.message || 'Retención emitida');
      await cargarRetenciones(facturaId);
    } catch (err) {
      await notify('error', 'Error', err.message || 'Error al emitir la retención');
    } finally {
      setAccionEnCurso(null);
    }
  };

  const abrirPreview = async (retencion) => {
    try {
      const res = await retencionesService.getById(retencion.id);
      const enriquecida = res.data;
      if (enriquecida.impuesto === 'iva') setModalPreviewIVA(enriquecida);
      else setModalPreviewISLR(enriquecida);
    } catch (err) {
      await notify('error', 'Error', err.message || 'Error al cargar el comprobante');
    }
  };

  const handleEliminar = async (factura) => {
    const ok = await confirm('Eliminar factura', `¿Eliminar la factura ${factura.numero_proveedor}? Esta acción no se puede deshacer.`);
    if (!ok) return;
    setAccionEnCurso(`eliminar-${factura.id}`);
    try {
      await facturasCompraService.delete(factura.id);
      await cargar();
      setFacturaAbierta(null);
    } catch (err) {
      await notify('error', 'Error', err.message || 'Error al eliminar la factura de compra');
    } finally {
      setAccionEnCurso(null);
    }
  };

  return (
    <div className="p-2 md:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-xl lg:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Truck className="text-blue-600" />
            Facturas de Compra
          </h2>
          <p className="text-sm text-gray-600">Facturas de proveedores, retenciones de IVA/ISLR y sus comprobantes.</p>
        </div>
        <button
          onClick={() => setShowNueva(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 hover:bg-blue-700 w-full sm:w-auto"
        >
          <Plus size={20} /> Nueva Factura de Compra
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-gray-600 text-sm">Total</p>
            <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-gray-600 text-sm">Registradas</p>
            <p className="text-2xl font-bold text-slate-600">{stats.registradas}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-gray-600 text-sm">Retenidas</p>
            <p className="text-2xl font-bold text-amber-600">{stats.retenidas}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-gray-600 text-sm">Pagadas</p>
            <p className="text-2xl font-bold text-emerald-600">{stats.pagadas}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-gray-600 text-sm">Monto total</p>
            <p className="text-xl font-bold text-gray-800">{fmtMonto(stats.monto_total)}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input
            type="text" placeholder="Buscar por número o proveedor..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          />
          <select value={proveedorFiltro} onChange={(e) => setProveedorFiltro(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg">
            <option value="">Todos los proveedores</option>
            {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
          </select>
          <select value={estadoFiltro} onChange={(e) => setEstadoFiltro(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg">
            <option value="">Todos los estados</option>
            {Object.entries(ESTADOS_FACTURA).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>}

      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : (
        <div className="space-y-3">
          {facturas.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400">Sin facturas de compra registradas</div>
          ) : facturas.map((factura) => {
            const estadoInfo = ESTADOS_FACTURA[factura.estado] || ESTADOS_FACTURA.registrada;
            const abierta = facturaAbierta === factura.id;
            const retenciones = retencionesPorFactura[factura.id] || [];
            const tieneRetIVA = retenciones.some(r => r.impuesto === 'iva');
            const tieneRetISLR = retenciones.some(r => r.impuesto === 'islr');
            const proveedor = proveedores.find(p => p.id === factura.proveedor_id);
            const esAgenteRetencion = proveedor?.agente_retencion || factura.proveedor_agente_retencion;

            return (
              <div key={factura.id} className="bg-white rounded-lg shadow-md border border-gray-200">
                <div className="flex justify-between items-center p-4 cursor-pointer" onClick={() => toggleFactura(factura)}>
                  <div className="flex-1 grid grid-cols-2 md:grid-cols-6 gap-3 items-center">
                    <div>
                      <p className="text-xs text-gray-500">Proveedor</p>
                      <p className="text-sm font-semibold text-gray-800 truncate">{factura.proveedor_nombre}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">N° Factura</p>
                      <p className="text-sm text-gray-800">{factura.numero_proveedor}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Fecha</p>
                      <p className="text-sm text-gray-800">{fmtFecha(factura.fecha_emision)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Total</p>
                      <p className="text-sm font-semibold text-gray-800">{fmtMonto(factura.total, factura.moneda)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Línea</p>
                      {factura.linea_negocio ? (
                        <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-700">{factura.linea_negocio}</span>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Sin asignar</span>
                      )}
                    </div>
                    <div>
                      <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${estadoInfo.className}`}>{estadoInfo.label}</span>
                    </div>
                  </div>
                  <button className="text-gray-500 p-2">
                    {abierta ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                </div>

                {abierta && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div><span className="text-gray-500">Base imponible:</span> {fmtMonto(factura.base_imponible, factura.moneda)}</div>
                      <div><span className="text-gray-500">Exento:</span> {fmtMonto(factura.base_exenta, factura.moneda)}</div>
                      <div><span className="text-gray-500">IVA ({fmtPct(factura.iva_porcentaje_aplicado)}):</span> {fmtMonto(factura.impuesto, factura.moneda)}</div>
                      <div><span className="text-gray-500">N° Control:</span> {factura.numero_control_proveedor || '—'}</div>
                    </div>

                    {!esAgenteRetencion && (
                      <p className="text-xs text-slate-500 italic">Este proveedor no está marcado como agente de retención afectado; puede retenerse igual si corresponde.</p>
                    )}

                    <div className="flex flex-wrap gap-2">
                      {factura.estado === 'registrada' && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); setFacturaEditar(factura); }}
                            className="flex items-center gap-2 px-3 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 text-sm"
                          >
                            <Edit2 size={16} /> Editar
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEliminar(factura); }}
                            disabled={accionEnCurso === `eliminar-${factura.id}`}
                            className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                          >
                            {accionEnCurso === `eliminar-${factura.id}` ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                            Eliminar
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleRetenerIVA(factura)}
                        disabled={tieneRetIVA || accionEnCurso === `iva-${factura.id}` || factura.estado === 'anulada'}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                      >
                        {accionEnCurso === `iva-${factura.id}` ? <Loader2 size={16} className="animate-spin" /> : <Landmark size={16} />}
                        {tieneRetIVA ? 'Retención IVA creada' : 'Retener IVA'}
                      </button>
                      <button
                        onClick={() => handleRetenerISLR(factura)}
                        disabled={tieneRetISLR || accionEnCurso === `islr-${factura.id}` || factura.estado === 'anulada'}
                        className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                      >
                        {accionEnCurso === `islr-${factura.id}` ? <Loader2 size={16} className="animate-spin" /> : <Percent size={16} />}
                        {tieneRetISLR ? 'Retención ISLR creada' : 'Retener ISLR'}
                      </button>
                      <button
                        onClick={() => setFacturaPago(factura)}
                        disabled={factura.estado === 'pagada' || factura.estado === 'anulada'}
                        className="flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                      >
                        <Wallet size={16} />
                        {factura.estado === 'pagada' ? 'Ya pagada' : 'Registrar Pago'}
                      </button>
                    </div>

                    {cargandoRetenciones === factura.id ? (
                      <div className="text-sm text-gray-400">Cargando retenciones...</div>
                    ) : retenciones.length > 0 && (
                      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                              <th className="px-3 py-2">Impuesto</th>
                              <th className="px-3 py-2">Base</th>
                              <th className="px-3 py-2">% Ret.</th>
                              <th className="px-3 py-2">Monto</th>
                              <th className="px-3 py-2">Comprobante</th>
                              <th className="px-3 py-2">Estado</th>
                              <th className="px-3 py-2">Acción</th>
                            </tr>
                          </thead>
                          <tbody>
                            {retenciones.map((r) => (
                              <tr key={r.id} className="border-b border-gray-100">
                                <td className="px-3 py-2 uppercase font-medium">{r.impuesto}</td>
                                <td className="px-3 py-2">{fmtMonto(r.base, r.moneda)}</td>
                                <td className="px-3 py-2">{fmtPct(r.porcentaje_aplicado)}</td>
                                <td className="px-3 py-2 font-semibold">{fmtMonto(r.monto, r.moneda)}</td>
                                <td className="px-3 py-2 text-gray-500">{r.comprobante || '—'}</td>
                                <td className="px-3 py-2">
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${
                                    r.estado === 'borrador' ? 'bg-slate-100 text-slate-600' : 'bg-emerald-100 text-emerald-700'
                                  }`}>
                                    {r.estado === 'borrador' ? <AlertCircle size={12} /> : <CheckCircle size={12} />}
                                    {r.estado}
                                  </span>
                                </td>
                                <td className="px-3 py-2">
                                  {r.estado === 'borrador' ? (
                                    <button
                                      onClick={() => handleEmitir(r, factura.id)}
                                      disabled={accionEnCurso === `emitir-${r.id}`}
                                      className="flex items-center gap-1 px-2 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700 text-xs disabled:bg-gray-300"
                                    >
                                      {accionEnCurso === `emitir-${r.id}` ? <Loader2 size={12} className="animate-spin" /> : null}
                                      Emitir
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => abrirPreview(r)}
                                      className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs"
                                    >
                                      <Printer size={12} /> PDF
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <ArchivosAdjuntosFactura facturaCompraId={factura.id} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showNueva && (
        <NuevaFacturaCompraModal
          proveedores={proveedores}
          servicios={servicios}
          centrosOperacion={centrosOperacion}
          onClose={() => setShowNueva(false)}
          onSuccess={() => { setShowNueva(false); cargar(); }}
        />
      )}

      {facturaEditar && (
        <NuevaFacturaCompraModal
          facturaInicial={facturaEditar}
          proveedores={proveedores}
          servicios={servicios}
          centrosOperacion={centrosOperacion}
          onClose={() => setFacturaEditar(null)}
          onSuccess={() => { setFacturaEditar(null); cargar(); }}
        />
      )}

      {modalPreviewIVA && (
        <RetencionEmitidaIVAPrintModal retencion={modalPreviewIVA} onClose={() => setModalPreviewIVA(null)} />
      )}
      {modalPreviewISLR && (
        <RetencionEmitidaISLRPrintModal retencion={modalPreviewISLR} onClose={() => setModalPreviewISLR(null)} />
      )}

      {facturaPago && (
        <RegistrarPagoModal
          factura={facturaPago}
          bancos={bancos}
          onClose={() => setFacturaPago(null)}
          onSuccess={() => { setFacturaPago(null); cargar(); }}
        />
      )}
      {ModalRenderer}
    </div>
  );
};

const ArchivosAdjuntosFactura = ({ facturaCompraId }) => {
  const { confirm, notify, ModalRenderer } = useModal();
  const [archivos, setArchivos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await facturasCompraArchivosService.getAll(facturaCompraId);
      setArchivos(res.data || []);
    } catch (err) {
      setError(err.message || 'Error al cargar archivos');
    } finally {
      setCargando(false);
    }
  }, [facturaCompraId]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleSubir = async (e) => {
    const archivo = e.target.files[0];
    if (!archivo) return;
    setError('');
    setSubiendo(true);
    try {
      await facturasCompraArchivosService.subir(facturaCompraId, archivo);
      await cargar();
    } catch (err) {
      setError(err.message || 'Error al adjuntar el archivo');
    } finally {
      setSubiendo(false);
      e.target.value = '';
    }
  };

  const handleEliminar = async (id) => {
    const ok = await confirm('Eliminar archivo', '¿Eliminar este archivo adjunto?');
    if (!ok) return;
    try {
      await facturasCompraArchivosService.eliminar(id);
      await cargar();
    } catch (err) {
      await notify('error', 'Error', err.message || 'Error al eliminar el archivo');
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-gray-700 flex items-center gap-1"><Paperclip size={14} /> Factura adjunta</p>
        {archivos.length < 3 && (
          <label className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 cursor-pointer">
            {subiendo ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Adjuntar
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleSubir} disabled={subiendo} className="hidden" />
          </label>
        )}
      </div>
      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
      {cargando ? (
        <p className="text-xs text-gray-400">Cargando...</p>
      ) : archivos.length === 0 ? (
        <p className="text-xs text-gray-400 italic">Sin archivos adjuntos todavía.</p>
      ) : (
        <div className="space-y-1">
          {archivos.map(a => (
            <div key={a.id} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1.5">
              <a
                href={`${UPLOADS_BASE_URL}/uploads/facturas_compra/${a.ruta_archivo}`}
                target="_blank" rel="noopener noreferrer"
                className="text-blue-600 hover:underline truncate"
              >
                {a.nombre_archivo}
              </a>
              <button onClick={() => handleEliminar(a.id)} className="text-red-500 hover:text-red-700 shrink-0 ml-2">
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
      {ModalRenderer}
    </div>
  );
};

const RegistrarPagoModal = ({ factura, bancos, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    fecha_pago: new Date().toISOString().split('T')[0],
    metodo_pago: 'transferencia',
    referencia_pago: '',
    banco_codigo: '',
    monto_pagado: factura.total || '',
    tasa_pago: '',
    tasa_pago_fuente: 'BCV'
  });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [buscandoTasa, setBuscandoTasa] = useState(true);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await configService.getTasaBcv();
        if (res.data?.valor) setForm(f => ({ ...f, tasa_pago: res.data.valor }));
      } catch {
        // Sin tasa automatica disponible hoy -- el campo queda vacio para llenarlo a mano.
      } finally {
        setBuscandoTasa(false);
      }
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setGuardando(true);
    try {
      const res = await facturasCompraService.registrarPago(factura.id, form);
      setResultado(res);
    } catch (err) {
      setError(err.message || 'Error al registrar el pago');
    } finally {
      setGuardando(false);
    }
  };

  if (resultado) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
          <div className="p-4 border-b flex justify-between items-center">
            <h3 className="text-lg font-bold">Pago registrado</h3>
            <button onClick={() => { onClose(); onSuccess(); }}><X size={20} /></button>
          </div>
          <div className="p-4 space-y-3">
            <p className="text-sm text-gray-600">El pago quedó registrado y la factura pasó a <strong>pagada</strong>.</p>
            {(resultado.retenciones_generadas?.iva || resultado.retenciones_generadas?.islr) && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800">
                Retenciones generadas automáticamente:{' '}
                {resultado.retenciones_generadas?.iva && 'IVA '}
                {resultado.retenciones_generadas?.islr && 'ISLR'}
              </div>
            )}
            {resultado.avisos?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 space-y-1">
                {resultado.avisos.map((a, i) => <p key={i}>{a}</p>)}
              </div>
            )}
            <div className="flex justify-end pt-2">
              <button onClick={() => { onClose(); onSuccess(); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Cerrar</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
          <h3 className="text-lg font-bold">Registrar Pago</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
          <p className="text-sm text-gray-600">
            Proveedor: <strong>{factura.proveedor_nombre}</strong> — {factura.numero_proveedor}
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de pago</label>
            <input type="date" value={form.fecha_pago} onChange={(e) => setForm(f => ({ ...f, fecha_pago: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Método de pago</label>
            <select value={form.metodo_pago} onChange={(e) => setForm(f => ({ ...f, metodo_pago: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
              <option value="transferencia">Transferencia</option>
              <option value="pago_movil">Pago móvil</option>
              <option value="efectivo">Efectivo</option>
              <option value="zelle">Zelle</option>
              <option value="otro">Otro</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Banco emisor</label>
            <select value={form.banco_codigo} onChange={(e) => setForm(f => ({ ...f, banco_codigo: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
              <option value="">— No aplica —</option>
              {bancos.map(b => <option key={b.codigo} value={b.codigo}>{b.nombre}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Referencia</label>
            <input value={form.referencia_pago} onChange={(e) => setForm(f => ({ ...f, referencia_pago: e.target.value }))} placeholder="N° de referencia/comprobante" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            <p className="text-xs text-gray-500 mt-1">Ayuda a conciliar este pago contra el movimiento real del banco.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Monto cancelado</label>
            <input type="number" step="0.01" value={form.monto_pagado} onChange={(e) => setForm(f => ({ ...f, monto_pagado: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tasa con la que se pagó</label>
              <div className="flex gap-1">
                <input
                  type="number" step="0.0001" value={form.tasa_pago}
                  onChange={(e) => setForm(f => ({ ...f, tasa_pago: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" required
                />
                {buscandoTasa && <Loader2 size={16} className="animate-spin text-gray-400 mt-2.5" />}
              </div>
              <p className="text-xs text-gray-500 mt-1">Precargada con la tasa BCV de hoy; ajústala si aplica.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fuente de la tasa</label>
              <select value={form.tasa_pago_fuente} onChange={(e) => setForm(f => ({ ...f, tasa_pago_fuente: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="BCV">BCV</option>
                <option value="paralelo">Paralelo</option>
                <option value="otro">Otra</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg">Cancelar</button>
            <button type="submit" disabled={guardando} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-400 flex items-center gap-2">
              {guardando && <Loader2 size={16} className="animate-spin" />} Registrar Pago
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const NuevaFacturaCompraModal = ({ facturaInicial, proveedores, servicios, centrosOperacion, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    proveedor_id: facturaInicial?.proveedor_id || '',
    numero_proveedor: facturaInicial?.numero_proveedor || '',
    numero_control_proveedor: facturaInicial?.numero_control_proveedor || '',
    fecha_emision: facturaInicial?.fecha_emision?.slice(0, 10) || new Date().toISOString().split('T')[0],
    moneda: facturaInicial?.moneda || 'USD',
    tasa: facturaInicial?.tasa || '',
    base_imponible: facturaInicial?.base_imponible || '',
    base_exenta: facturaInicial?.base_exenta || '0',
    factura_conforme: facturaInicial?.factura_conforme !== false,
    notas: facturaInicial?.notas || '',
    id_servicio: facturaInicial?.id_servicio || '',
    centro_operacion_id: facturaInicial?.centro_operacion_id || ''
  });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [buscandoTasa, setBuscandoTasa] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleUsarTasaBcv = async () => {
    setBuscandoTasa(true);
    try {
      const res = await configService.getTasaBcv();
      if (res.data?.valor) setForm(prev => ({ ...prev, tasa: res.data.valor }));
      else setError(res.message || 'No se pudo obtener la tasa BCV');
    } catch (err) {
      setError(err.message || 'No se pudo obtener la tasa BCV, ingrésela manualmente');
    } finally {
      setBuscandoTasa(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.proveedor_id || !form.numero_proveedor || !form.fecha_emision) {
      setError('Proveedor, número de factura y fecha son requeridos');
      return;
    }
    if (!form.tasa) {
      setError('La tasa de cambio es requerida (incluso en USD): sin ella no se puede emitir el comprobante de retención en bolívares');
      return;
    }
    setGuardando(true);
    try {
      const payload = {
        ...form,
        proveedor_id: parseInt(form.proveedor_id, 10),
        tasa: parseFloat(form.tasa),
        base_imponible: parseFloat(form.base_imponible || 0),
        base_exenta: parseFloat(form.base_exenta || 0),
        id_servicio: form.id_servicio ? parseInt(form.id_servicio, 10) : null,
        centro_operacion_id: form.centro_operacion_id ? parseInt(form.centro_operacion_id, 10) : null
      };
      if (facturaInicial) {
        await facturasCompraService.update(facturaInicial.id, payload);
      } else {
        await facturasCompraService.create(payload);
      }
      onSuccess();
    } catch (err) {
      setError(err.message || 'Error al registrar la factura de compra');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-bold">{facturaInicial ? 'Editar Factura de Compra' : 'Nueva Factura de Compra'}</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Proveedor</label>
            <select name="proveedor_id" value={form.proveedor_id} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required>
              <option value="">Selecciona un proveedor...</option>
              {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}{p.rif ? ` (${p.rif})` : ''}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Línea de negocio</label>
            <select name="id_servicio" value={form.id_servicio} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
              <option value="">Sin asignar</option>
              {servicios.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
            </select>
            <p className="text-xs text-gray-500 mt-1">Necesaria para que el gasto aparezca en Reportes → Egresos por línea de negocio.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Centro de operación (destino de la mercancía)</label>
            <select name="centro_operacion_id" value={form.centro_operacion_id} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
              <option value="">Sin especificar</option>
              {centrosOperacion.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">N° Factura</label>
              <input name="numero_proveedor" value={form.numero_proveedor} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">N° Control</label>
              <input name="numero_control_proveedor" value={form.numero_control_proveedor} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de emisión</label>
              <input type="date" name="fecha_emision" value={form.fecha_emision} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Moneda</label>
              <select name="moneda" value={form.moneda} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="USD">USD</option>
                <option value="VES">VES</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tasa de cambio (BCV)</label>
            <div className="flex gap-2">
              <input type="number" step="0.0001" name="tasa" value={form.tasa} onChange={handleChange} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg" required />
              <button type="button" onClick={handleUsarTasaBcv} disabled={buscandoTasa} className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-1 whitespace-nowrap disabled:opacity-50">
                {buscandoTasa ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Tasa BCV de hoy
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Requerida incluso en USD: es la que convierte a bolívares el comprobante de retención.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Base imponible</label>
              <input type="number" step="0.01" name="base_imponible" value={form.base_imponible} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Base exenta</label>
              <input type="number" step="0.01" name="base_exenta" value={form.base_exenta} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" name="factura_conforme" checked={form.factura_conforme} onChange={handleChange} />
            Factura conforme (cumple requisitos SENIAT)
          </label>
          <p className="text-xs text-gray-500 -mt-2">Si no es conforme, la retención de IVA aplica al 100% en vez de 75%.</p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
            <textarea name="notas" value={form.notas} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" rows={2} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg">Cancelar</button>
            <button type="submit" disabled={guardando} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center gap-2">
              {guardando && <Loader2 size={16} className="animate-spin" />}
              {facturaInicial ? 'Guardar cambios' : 'Registrar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FacturasCompraView;
